/* eslint-disable no-nested-ternary */
import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Box, SvgIconProps, Theme,
  ThemeProvider, TextField,
  Typography, InputAdornment,
  Tooltip,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { SystemStyleObject } from '@mui/system';
import type { InputProps } from './Input.types';
import {
  inputSizeSpecs, inputDisabledStyles, inputFilledStyles,
} from './Input.styles';
import theme from '../../../assets/theme';

const Input: React.FC<InputProps> = ({
  size = 'm',
  customVariant = 'default',
  error,
  disabled,
  placeholderText,
  leadingIcon,
  trailingIcon,
  helpIcon,
  helpIconText,
  helperText,
  required,
  label,
  value,
  onInputChange,
  width,
  minWidth,
  maxWidth,
  iconProps,
  sx,
  labelSx,
  helperTextSx,
  textFieldSx,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value || ''); // Initialize state with the value prop or an empty string

  // Sync inputValue with value prop changes
  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputValue(event.target.value); // Update local state
    if (onInputChange) {
      // pass data to the parent component
      onInputChange(event);
    }
  };

  /*
  ** Helper to safely render icons passed as either JSX (<Icon />) or component refs (Icon)
  */
  const renderIcon = (
    icon?: React.ReactNode | React.ElementType<SvgIconProps> | undefined,
    iconPassedProps?: SvgIconProps,
  ) => {
    if (!icon) return null;
    // If JSX (<Icon />), allow explicit element props to win and return
    if (React.isValidElement<SvgIconProps>(icon)) {
      return React.cloneElement(icon, {
        ...iconPassedProps,
        sx: {
          ...icon.props.sx,
          height: iconPassedProps?.height || 20,
          width: iconPassedProps?.width || 20,
        },
      });
    }
    // Else instantiate it from the component ref (Icon)
    const IconComp = icon as React.ElementType<SvgIconProps>;
    return (
      <IconComp
        {...iconPassedProps}
        sx={{
          height: iconPassedProps?.height || 20,
          width: iconPassedProps?.width || 20,
          ...iconPassedProps?.sx,
        }}
      />
    );
  };

  /**
   * Constructs a style object for dimensions based on provided width, minWidth, and maxWidth.
   *
   * @param width - The width of the element. If undefined,
   * it will not be included in the style object.
   * @param minWidth - The minimum width of the element.
   * If undefined, it will not be included in the style object.
   * @param maxWidth - The maximum width of the element.
   * If undefined, it will not be included in the style object.
   * @returns A SystemStyleObject containing the specified dimensions.
   */
  const dimensionOverrides: SystemStyleObject<Theme> = {
    ...(width !== undefined ? { width } : {}),
    ...(minWidth !== undefined ? { minWidth } : {}),
    ...(maxWidth !== undefined ? { maxWidth } : {}),
  };

  return (
    <>
      <Box
        sx={(muiTheme: Theme) => ({
          display: 'flex',
          width: muiTheme.spacing(40),
          minWidth: muiTheme.spacing(40),
          ...(!(sx && 'minWidth' in sx) && {
            [muiTheme.breakpoints.down('sm')]: {
              minWidth: '100%',
            },
          }),
          ...(!(sx && 'width' in sx) && {
            [muiTheme.breakpoints.down('sm')]: {
              width: '100%',
            },
          }),
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: muiTheme.spacing(0.75),
          ...dimensionOverrides,
          ...sx,
        })}
      >
        {label && <Box
          sx={(muiTheme: Theme) => ({
            display: 'flex',
            alignItems: 'flex-start',
            gap: muiTheme.spacing(0.25),
            ...labelSx,
          })}><Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              ...labelSx,
            }}
          >{label}
            {required && <span>*</span>}
          </Typography>
        </Box>
        }
        <TextField
          id="input-with-icon-textfield"
          disabled={!!disabled}
          placeholder={placeholderText}
          InputProps={{
            ...((trailingIcon || helpIcon) && {
              endAdornment: (
                <InputAdornment
                  position={trailingIcon ? 'end' : 'start'}
                  sx={{
                    marginRight: 0,
                    ...(trailingIcon && { gap: 0.5, display: 'flex' }),
                  }}
                >
                  {trailingIcon && renderIcon(trailingIcon, iconProps)}
                  {helpIcon && (
                    error ? <ErrorOutlineOutlinedIcon
                      sx={(muiTheme: Theme) => ({
                        color: theme.palette.error.main,
                        width: muiTheme.spacing(2),
                        height: muiTheme.spacing(2),
                      })}
                    /> : (
                      helpIconText ? (
                        <Tooltip title={helpIconText} placement='top'>
                          <HelpOutlineIcon
                            sx={(muiTheme: Theme) => ({
                              width: muiTheme.spacing(2),
                              height: muiTheme.spacing(2),
                            })}
                          />
                        </Tooltip>
                      ) : (
                        <HelpOutlineIcon
                          sx={(muiTheme: Theme) => ({
                            width: muiTheme.spacing(2),
                            height: muiTheme.spacing(2),
                          })}
                        />
                      )
                    )
                  )}
                </InputAdornment>
              ),
            }),
            ...(customVariant === 'iconLeading' && leadingIcon && {
              startAdornment: (
                <InputAdornment position="start">
                  {renderIcon(leadingIcon, iconProps)}
                </InputAdornment>
              ),
            }),
          }}
          value={inputValue} // Controlled value
          onChange={(e) => handleOnChange(e)} // Update state on user input
          {...props}
          sx={(muiTheme: Theme) => ({
            width: '100%',
            minWidth: 'inherit',
            maxWidth: 'inherit',
            '& .MuiOutlinedInput-root': {
              ...inputSizeSpecs[size],
              ...inputFilledStyles(muiTheme, error),
              ...(disabled ? inputDisabledStyles(muiTheme, error) : {}),
              ...textFieldSx,
            },
          })}
        />
        {helperText && <Typography
          sx={(muiTheme: Theme) => ({
            fontSize: '14px',
            fontWeight: 400,
            color: error ? theme.palette.error.main : muiTheme.palette.grey[600],
            ...helperTextSx,
          })}
        >{helperText}</Typography>}
      </Box>
    </>
  );
};

const OptimusInput: React.FC<React.ComponentProps<typeof Input>> = (props) => (
  <ThemeProvider theme={theme}>
    <Input {...props} />
  </ThemeProvider>
);

export default OptimusInput;
